// src/Dashboard/pages/user/Inscription/components/DossierDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  FaBell,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaEdit,
  FaEuroSign,
  FaFileMedical,
  FaFilePdf,
  FaFileSignature,
  FaFolderOpen,
  FaGoogleDrive,
  FaRedoAlt,
  FaSave,
  FaThumbsUp,
  FaTimes,
  FaUpload,
  FaIdCard,
  FaLock,
} from "react-icons/fa";
import { toast } from "react-toastify";

import DrawerShell from "./DrawerShell";
import {
  computeDossierSummary,
  computeReglementTotal,
  formatDateFR,
  formatEUR,
  getAdherentAvatar,
  getDocumentMeta,
  getLicenceInsuranceUrl,
  getLicenceSignedUrl,
  getLicenceToSignUrl,
  getPaymentStatusMeta,
  openInNewTab,
  isReglementValidated,
} from "../utils/inscriptionUtils";

import { sendNotificationToAdmin } from "../../../../../services/notificationServices";
import { updateReglementInscription } from "../../../../../services/reglementinscriptionServices";

const PAYMENT_TYPE_OPTIONS = [
  { value: "", label: "— Sélectionner —" },
  { value: "En ligne", label: "En ligne" },
  { value: "Chèque", label: "Chèque" },
  { value: "Espèces", label: "Espèces" },
  { value: "Virement", label: "Virement" },
  { value: "CB", label: "CB" },
  { value: "Autre", label: "Autre" },
];

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function StepCard({ label, done }) {
  return (
    <div
      className={`rounded-2xl border px-4 py-3 ${
        done
          ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
          : "border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-400"
      }`}
    >
      <div className="flex items-center gap-2 text-sm font-bold">
        <FaCheckCircle
          className={done ? "text-emerald-600" : "text-slate-400"}
        />
        {label}
      </div>
    </div>
  );
}

function DocumentTile({ title, url, icon, help }) {
  const meta = getDocumentMeta(url);

  return (
    <button
      type="button"
      onClick={() => url && openInNewTab(url)}
      disabled={!url}
      className={`rounded-[22px] border p-4 text-left transition ${
        url
          ? "border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:hover:bg-slate-900"
          : "cursor-not-allowed border-dashed border-slate-300 bg-slate-50 opacity-75 dark:border-slate-700 dark:bg-slate-900/50"
      }`}
    >
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-[#013E8E] dark:bg-slate-900 dark:text-indigo-300">
          {icon}
        </span>

        <div className="min-w-0">
          <p className="font-bold text-slate-900 dark:text-white">{title}</p>

          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {meta.label}
          </p>

          {help && (
            <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
              {help}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}

function PaymentTypeEditor({
  reglement,
  value,
  onChange,
  editing,
  setEditing,
  saving,
  onSave,
}) {
  if (!reglement) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-400">
        Le type de paiement pourra être renseigné lorsqu’un règlement existera
        pour cette saison.
      </div>
    );
  }

  if (!editing) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/50">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
              Type de paiement
            </p>

            <p className="mt-1 text-base font-bold text-slate-900 dark:text-white">
              {value || "—"}
            </p>

            <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
              Vous pouvez indiquer ou corriger le mode de paiement utilisé.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaEdit />
            Modifier
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-blue-200 bg-blue-50/70 p-4 dark:border-blue-500/20 dark:bg-blue-500/10">
      <label
        htmlFor="type-reglement"
        className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700 dark:text-blue-300"
      >
        Modifier le type de paiement
      </label>

      <div className="mt-3 grid gap-2 sm:grid-cols-[minmax(0,1fr),auto,auto]">
        <select
          id="type-reglement"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={saving}
          className="h-11 w-full rounded-2xl border border-blue-200 bg-white px-3 text-sm font-bold text-slate-800 outline-none transition focus:border-[#013E8E] focus:ring-4 focus:ring-blue-500/10 disabled:opacity-60 dark:border-blue-500/20 dark:bg-slate-950 dark:text-white"
        >
          {PAYMENT_TYPE_OPTIONS.map((option) => (
            <option key={option.label} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <FaSave />
          {saving ? "Enregistrement..." : "Enregistrer"}
        </button>

        <button
          type="button"
          onClick={() => {
            onChange(reglement?.type_reglement || "");
            setEditing(false);
          }}
          disabled={saving}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <FaTimes />
          Annuler
        </button>
      </div>
    </div>
  );
}

export default function DossierDrawer({
  isOpen,
  onClose,
  adherent,
  dossier,
  reglement,
  groups,
  user,
  settings,
  activeSeason,
  onOpenDocuments,
  onOpenRenewal,
  onOpenTrial,
  onGoPayments,
  onGoAttestation,
  onReglementUpdated,
  onGoMemberCard,
}) {
  const [editingType, setEditingType] = useState(false);
  const [typeReglementLocal, setTypeReglementLocal] = useState("");
  const [savingType, setSavingType] = useState(false);

  const summary = useMemo(() => {
    return computeDossierSummary({
      adherent,
      dossier,
      reglement,
      activeSeason,
    });
  }, [activeSeason, adherent, dossier, reglement]);

  useEffect(() => {
    setTypeReglementLocal(reglement?.type_reglement || "");
    setEditingType(false);
  }, [reglement?.id, reglement?.type_reglement]);

  if (!adherent) return null;

  const licenceSignedUrl = getLicenceSignedUrl(dossier);
  const licenceInsuranceUrl = getLicenceInsuranceUrl(dossier);
  const licenceToSignUrl = getLicenceToSignUrl(dossier);
  const paymentMeta = getPaymentStatusMeta(reglement?.statut_reglement);
  const total = computeReglementTotal(reglement);
  const isTrial = adherent.statut_inscription === "Cours d'essai";
  const canGenerateMemberCard = isReglementValidated(dossier);

  const notifyDriveDeposit = async () => {
    if (!dossier?.lien_drive) return;

    try {
      const notification = `${user?.prenom || ""} ${
        user?.nom || ""
      } a déposé des documents pour ${adherent.prenom} ${
        adherent.nom
      } dans Google Drive.`;

      await sendNotificationToAdmin(notification, user?.token);
      toast.success("Notification envoyée aux administrateurs.");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l’envoi de la notification.");
    }
  };

  const handleSavePaymentType = async () => {
    if (!reglement?.id) {
      toast.error("Aucun règlement à modifier pour cette saison.");
      return;
    }

    try {
      setSavingType(true);

      const payload = {
        id: reglement.id,
        type_reglement: typeReglementLocal || null,
      };

      await updateReglementInscription(payload);

      setEditingType(false);

      toast.success("Type de paiement mis à jour.");

      if (typeof onReglementUpdated === "function") {
        await onReglementUpdated({
          ...reglement,
          type_reglement: typeReglementLocal || null,
        });
      }
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour du type de paiement.");
    } finally {
      setSavingType(false);
    }
  };

  return (
    <DrawerShell
      isOpen={isOpen}
      onClose={onClose}
      title={`Dossier de ${adherent.prenom} ${adherent.nom}`}
      subtitle={`Saison active : ${activeSeason || "—"}`}
      icon={<FaFolderOpen />}
      width="max-w-5xl"
    >
      <div className="space-y-5">
        <section
          className={`overflow-hidden rounded-[26px] border p-5 ring-1 ${summary.globalClassName}`}
        >
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <img
                src={getAdherentAvatar(adherent)}
                alt={`${adherent.prenom} ${adherent.nom}`}
                className="h-16 w-16 rounded-2xl object-cover ring-1 ring-white/40"
              />

              <div>
                <h3 className="text-xl font-bold">
                  {(adherent.nom || "").toUpperCase()} {adherent.prenom || ""}
                </h3>

                <p className="mt-1 text-sm opacity-80">
                  Licence : {adherent.licence_number || "—"} · Groupe :{" "}
                  {groups?.length ? groups.join(", ") : "Non attribué"}
                </p>

                <div className="mt-3 inline-flex rounded-full bg-white/70 px-3 py-1 text-sm font-bold dark:bg-slate-950/40">
                  {summary.globalLabel}
                </div>
              </div>
            </div>

            {!summary.isCurrentSeason && (
              <button
                type="button"
                onClick={onOpenRenewal}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-orange-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-orange-700"
              >
                <FaRedoAlt />
                Renouvellement adhésion
              </button>
            )}
          </div>
        </section>

        {!dossier && (
          <div className="rounded-[24px] border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
            Aucun dossier d’inscription n’existe encore pour cet adhérent. Les
            documents ne pourront être téléversés qu’une fois le dossier créé
            côté serveur.
          </div>
        )}

        <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="rounded-[24px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950">
            <h4 className="text-base font-bold text-slate-900 dark:text-white">
              Documents à fournir
            </h4>

            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Ces éléments permettent au bureau de vérifier votre dossier.
            </p>

            <div className="mt-4 grid grid-cols-1 gap-3">
              {summary.requiredSteps.map((step) => (
                <StepCard key={step.key} label={step.label} done={step.done} />
              ))}
            </div>
          </div>

          <div className="rounded-[24px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950">
            <h4 className="text-base font-bold text-slate-900 dark:text-white">
              Validation par le bureau
            </h4>

            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Vous déposez les documents, le bureau les valide ensuite.
            </p>

            <div className="mt-4 grid grid-cols-1 gap-3">
              {summary.validationSteps.map((step) => (
                <StepCard key={step.key} label={step.label} done={step.done} />
              ))}
            </div>
          </div>
        </section>

        <section className="rounded-[24px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h4 className="text-base font-bold text-slate-900 dark:text-white">
                Documents du dossier
              </h4>

              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Cliquez sur un document pour le consulter, ou utilisez le bouton
                pour téléverser / remplacer.
              </p>
            </div>

            <button
              type="button"
              onClick={onOpenDocuments}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800"
            >
              <FaUpload />
              Téléverser / modifier
            </button>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
            <DocumentTile
              title="Certificat / Attestation"
              url={dossier?.cm_url}
              icon={<FaFileMedical />}
              help={
                dossier?.date_cm
                  ? `Date du document : ${formatDateFR(dossier.date_cm)}`
                  : "La date du document médical est importante."
              }
            />

            <DocumentTile
              title="Formulaire licence signé"
              url={licenceSignedUrl}
              icon={<FaFileSignature />}
              help={
                licenceToSignUrl
                  ? "Un formulaire vierge est disponible au téléchargement."
                  : "Téléversez le formulaire signé."
              }
            />

            <DocumentTile
              title="Licence / Assurance"
              url={licenceInsuranceUrl}
              icon={<FaFilePdf />}
              help="Document optionnel ou transmis après validation fédérale."
            />
          </div>
        </section>

        <section className="rounded-[24px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-950">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-[#013E8E] dark:bg-slate-900 dark:text-indigo-300">
              <FaEuroSign />
            </span>

            <div className="min-w-0 flex-1">
              <h4 className="text-base font-bold text-slate-900 dark:text-white">
                Règlement licence-cotisation
              </h4>

              {reglement ? (
                <div className="mt-3 space-y-3 text-sm text-slate-700 dark:text-slate-300">
                  <div className="flex flex-wrap items-center gap-2">
                    <span>Statut :</span>

                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-bold ${paymentMeta.className}`}
                    >
                      {paymentMeta.label}
                    </span>
                  </div>

                  <PaymentTypeEditor
                    reglement={reglement}
                    value={typeReglementLocal}
                    onChange={setTypeReglementLocal}
                    editing={editingType}
                    setEditing={setEditingType}
                    saving={savingType}
                    onSave={handleSavePaymentType}
                  />

                  <div className="grid gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm dark:border-slate-800 dark:bg-slate-900/50 sm:grid-cols-2">
                    <p>
                      Licence / assurance :{" "}
                      <span className="font-bold text-slate-900 dark:text-white">
                        {formatEUR(reglement.montant_lic_assurance)}
                      </span>
                    </p>

                    <p>
                      Cotisation :{" "}
                      <span className="font-bold text-slate-900 dark:text-white">
                        {formatEUR(reglement.montant_cotisation)}
                      </span>
                    </p>

                    <p>
                      Réduction foyer :{" "}
                      <span className="font-bold text-slate-900 dark:text-white">
                        {formatEUR(reglement.reduction_foyer)}
                      </span>
                    </p>

                    <p>
                      Pass’Sport :{" "}
                      <span className="font-bold text-slate-900 dark:text-white">
                        {formatEUR(reglement.pass_sport)}
                      </span>
                    </p>

                    <p>
                      Pass'Club 32 :{" "}
                      <span className="font-bold text-slate-900 dark:text-white">
                        {formatEUR(reglement.passclub32)}
                      </span>
                    </p>

                    <p>
                      Réduction saison :{" "}
                      <span className="font-bold text-slate-900 dark:text-white">
                        {formatEUR(reglement.reduction_saison)}
                      </span>
                    </p>

                    <p className="font-bold text-slate-900 dark:text-white">
                      Total : {formatEUR(total)}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="mt-3 space-y-3">
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Aucun règlement enregistré pour la saison active.
                  </p>

                  <PaymentTypeEditor
                    reglement={reglement}
                    value={typeReglementLocal}
                    onChange={setTypeReglementLocal}
                    editing={editingType}
                    setEditing={setEditingType}
                    saving={savingType}
                    onSave={handleSavePaymentType}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onGoPayments}
              className="inline-flex items-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-2.5 text-sm font-bold text-white transition hover:bg-indigo-800"
            >
              <FaEuroSign />
              Accéder aux règlements
            </button>

            <button
              type="button"
              onClick={() => canGenerateMemberCard && onGoMemberCard?.(adherent, dossier)}
              disabled={!canGenerateMemberCard}
              title={
                canGenerateMemberCard
                  ? "Créer la carte de membre"
                  : "Disponible uniquement lorsque le règlement est validé"
              }
              className={cn(
                "inline-flex items-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-bold transition",
                canGenerateMemberCard
                  ? "bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
                  : "cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-500",
              )}
            >
              {canGenerateMemberCard ? <FaIdCard /> : <FaLock />}
              Carte membre
            </button>

            {dossier?.validate_reglement && (
              <button
                type="button"
                onClick={() => onGoAttestation?.(adherent, reglement)}
                className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-emerald-700"
              >
                <FaFilePdf />
                Attestation d’inscription
              </button>
            )}
          </div>
        </section>

        <section className="grid grid-cols-1 gap-3 md:grid-cols-2 pb-12">
          {licenceToSignUrl && (
            <button
              type="button"
              onClick={() => openInNewTab(licenceToSignUrl)}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-700"
            >
              <FaFileSignature />
              Télécharger le formulaire à signer
            </button>
          )}

          {dossier?.lien_drive && (
            <>
              <button
                type="button"
                onClick={() => openInNewTab(dossier.lien_drive)}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-sky-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-sky-700"
              >
                <FaGoogleDrive />
                Ouvrir le Google Drive
              </button>

              <button
                type="button"
                onClick={notifyDriveDeposit}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
              >
                <FaBell />
                Signaler un dépôt
              </button>
            </>
          )}

          {isTrial && (
            <button
              type="button"
              onClick={onOpenTrial}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-violet-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-violet-700"
            >
              <FaThumbsUp />
              Validation du cours d’essai
            </button>
          )}

          {summary.globalStatus === "waiting" && (
            <div className="inline-flex items-center justify-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm font-bold text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300">
              <FaClock />
              En attente de validation bureau
            </div>
          )}

          {summary.globalStatus === "up_to_date" && (
            <div className="inline-flex items-center justify-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
              <FaCheckCircle />
              Dossier complet et validé
            </div>
          )}
        </section>
      </div>
    </DrawerShell>
  );
}
